import ContentPage from '@/components/feature/ContentPage';

export default function SpreadsheetPage() {
  return (
    <ContentPage
      title="CSSBuy Spreadsheet — Complete 2026 Guide"
      metaTitle="CSSBuy Spreadsheet 2026: Complete Guide to Finding Products | CSSBuyBR"
      metaDescription="Complete CSSBuy Spreadsheet guide 2026. Learn how to find, compare and organize products. Categories, QC tips, shipping info and safety advice."
      subtitle="Everything you need to know about using CSSBuy Spreadsheet to find and organize products efficiently."
      showToc
      sections={[
        { id: 'what', title: 'What Is CSSBuy Spreadsheet?' },
        { id: 'how', title: 'How It Works' },
        { id: 'categories', title: 'Product Categories' },
        { id: 'qc', title: 'Quality Control (QC)' },
        { id: 'shipping', title: 'Shipping & Costs' },
        { id: 'safety', title: 'Safety Tips' },
        { id: 'faq', title: 'FAQ' },
      ]}
      content={
        <div className="space-y-6">
          <section id="what">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">What Is CSSBuy Spreadsheet?</h2>
            <p>A CSSBuy Spreadsheet is an organized resource that brings together product links, categories, and useful information for CSSBuy users. It helps both beginners and experienced buyers navigate the platform more efficiently by providing pre-organized access to products across multiple categories.</p>
            <p>These spreadsheets typically include direct links to product pages, size guides, price comparisons, and community notes on quality and accuracy.</p>
          </section>

          <section id="how">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">How CSSBuy Spreadsheet Works</h2>
            <p>Using a CSSBuy Spreadsheet is straightforward. Open the spreadsheet, browse the organized categories, and click through to products you are interested in. Each entry typically includes:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Product name and description</li>
              <li>Direct link to the CSSBuy product page</li>
              <li>Price information and currency</li>
              <li>Size and availability details</li>
              <li>Community notes or QC references</li>
            </ul>
          </section>

          <section id="categories">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Product Categories in CSSBuy Spreadsheet</h2>
            <p>CSSBuy spreadsheets typically organize products into clear categories. The most common categories include:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Shoes & Sneakers', 'Hoodies & Sweaters', 'T-Shirts', 'Jackets & Outerwear',
                'Pants & Shorts', 'Headwear', 'Bags & Backpacks', 'Accessories',
                'Belts', 'Perfumes', 'Underwear', 'Sportswear & Jerseys'
              ].map((cat, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-500"></i>
                  {cat}
                </div>
              ))}
            </div>
            <p>Each category contains curated links to relevant products, making it easy to find what you need without endless searching.</p>
          </section>

          <section id="qc">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Quality Control (QC) in CSSBuy</h2>
            <p>Quality Control is one of the most important aspects of using CSSBuy. Before purchasing, always:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Request QC photos when available</li>
              <li>Compare product photos with reference images</li>
              <li>Check community reviews for specific items</li>
              <li>Understand the return and exchange policies</li>
              <li>Verify seller ratings and transaction history</li>
            </ul>
          </section>

          <section id="shipping">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Shipping & Costs</h2>
            <p>Shipping costs through CSSBuy vary based on several factors:</p>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Factor</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    ['Weight', 'Heavier items increase shipping costs significantly'],
                    ['Volume', 'Bulky items may trigger dimensional weight charges'],
                    ['Destination', 'Different regions have different rate structures'],
                    ['Method', 'Express is faster but more expensive than standard'],
                    ['Insurance', 'Optional protection against loss or damage'],
                  ].map(([k, v], i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium text-foreground-800">{k}</td>
                      <td className="px-3 py-2 text-foreground-600">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>Always confirm current shipping rates before finalizing any purchase, as prices and policies may change.</p>
          </section>

          <section id="safety">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Safety Tips</h2>
            <p>When using CSSBuy spreadsheets and resources, protect yourself by following these guidelines:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Verify all URLs before clicking — look for official domains</li>
              <li>Never share payment details outside official channels</li>
              <li>Use strong, unique passwords for your accounts</li>
              <li>Be cautious of deals that seem too good to be true</li>
              <li>Check community feedback before buying from unfamiliar sellers</li>
              <li>Keep records of all transactions and communications</li>
            </ul>
          </section>

          <section id="faq">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">What is W2C?</h3>
                <p className="text-sm">W2C means "Where To Cop" — a common term in shopping communities indicating where to find a specific product.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">Are CSSBuy spreadsheets free to use?</h3>
                <p className="text-sm">Most community spreadsheets are shared freely. Always verify the source before using any resource.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">How often are spreadsheets updated?</h3>
                <p className="text-sm">Update frequency varies. Check the last updated date when available, and cross-reference with current listings.</p>
              </div>
            </div>
          </section>

          <div className="mt-10 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Ready to explore?</h3>
            <p className="text-sm text-primary-700 mb-3">Visit the main website to browse the full CSSBuy product catalog and start shopping.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}